import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect } from 'react';

import { CONTEXT_MANAGEMENT_LABEL } from '../../constants/stages';
import { ProjectStatus } from '../../types/project';

interface SearchAndFilterProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  selectedStatus: ProjectStatus | 'all';
  onStatusChange: (status: ProjectStatus | 'all') => void;
  onClearFilters: () => void;
}

/**
 * SearchAndFilter component for filtering and searching projects
 * Provides search input and status filter dropdown
 */
export const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  searchTerm,
  onSearchChange,
  selectedStatus,
  onStatusChange,
  onClearFilters,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const statusOptions = [
    { value: 'all', label: 'All Projects' },
    { value: ProjectStatus.CONTEXT_UPLOAD, label: CONTEXT_MANAGEMENT_LABEL },
    { value: ProjectStatus.CONTEXT_REVIEW, label: 'Context Analysis' },
    { value: ProjectStatus.ADR, label: 'ADR' },
    { value: ProjectStatus.GENERATION, label: 'DSI Generation' },
    {
      value: ProjectStatus.BIZZDESIGN_INTEGRATION,
      label: 'Bizzdesign Integration',
    },
    { value: ProjectStatus.COMPLETED, label: 'Completed' },
    { value: ProjectStatus.PUBLISHED, label: 'Published' },
  ];

  const hasActiveFilters = searchTerm !== '' || selectedStatus !== 'all';

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-6">
      {/* Search Input */}
      <div className="relative flex-1">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#0097a9] focus:border-[#0097a9]"
          placeholder="Search projects by title or description..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      {/* Status Filter Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0097a9] gap-2"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <FunnelIcon className="w-4 h-4" />
          Filter
          {selectedStatus !== 'all' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#336094]/10 text-[#336094]">
              1
            </span>
          )}
        </button>
        
        {isFilterOpen && (
          <div className="absolute right-0 z-10 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
            <div className="py-1" role="menu">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  className={`flex w-full justify-between px-4 py-2 text-sm text-left hover:bg-gray-100 ${
                    selectedStatus === option.value ? 'bg-[#336094]/5 text-[#336094]' : 'text-gray-700'
                  }`}
                  onClick={() => {
                    onStatusChange(option.value as ProjectStatus | 'all');
                    setIsFilterOpen(false);
                  }}
                >
                  <span>{option.label}</span>
                  {selectedStatus === option.value && (
                    <div className="w-2 h-2 bg-[#336094] rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <button
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0097a9] gap-2"
          onClick={onClearFilters}
        >
          <XMarkIcon className="w-4 h-4" />
          Clear
        </button>
      )}
    </div>
  );
};
